import { useState, useEffect } from "react";
import {
  collection, getDocs, query, where, orderBy,
  addDoc, deleteDoc, doc, serverTimestamp, limit
} from "firebase/firestore";
import { db } from "../firebase";
import Icon from "./Icon";
import { formatDate } from "../utils";

// A trip is shared when more than one person is a member
const isShared = (t) => (t?.memberIds?.length || 1) > 1;

export default function MergeTripModal({ trip, user, onClose, onMerged }) {
  const [allTrips, setAllTrips] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    const load = async () => {
      // Scope to trips within ±90 days of this trip's start date to avoid
      // loading all 165+ trips. Falls back to 30 most recent if no startDate.
      const anchor = trip.startDate || null;
      let snap;

      if (anchor) {
        const anchorMs = new Date(anchor).getTime();
        const pad = 90 * 24 * 60 * 60 * 1000;
        const lo = new Date(anchorMs - pad).toISOString().split("T")[0];
        const hi = new Date(anchorMs + pad).toISOString().split("T")[0];
        snap = await getDocs(query(
          collection(db, "trips"),
          where("memberIds", "array-contains", user.uid),
          where("startDate", ">=", lo),
          where("startDate", "<=", hi),
          orderBy("startDate", "desc")
        ));
      } else {
        snap = await getDocs(query(
          collection(db, "trips"),
          where("memberIds", "array-contains", user.uid),
          orderBy("startDate", "desc"),
          limit(31)
        ));
      }

      const trips = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(t => t.id !== trip.id)
        .slice(0, 30);

      setAllTrips(trips);
      setLoading(false);
    };
    load();
  }, [trip.id, trip.startDate, user.uid]);

  const handleMerge = async () => {
    if (!selected) return;
    // Safety: never delete a shared trip as part of a merge (other travelers would lose it)
    if (isShared(selected)) return;
    setMerging(true);

    try {
      // Fetch all segments from the selected (source) trip
      const segsSnap = await getDocs(
        collection(db, "trips", selected.id, "segments")
      );

      // Copy each segment into the current (target) trip
      const targetSegsRef = collection(db, "trips", trip.id, "segments");
      for (const segDoc of segsSnap.docs) {
        const data = segDoc.data();
        await addDoc(targetSegsRef, {
          ...data,
          createdAt: serverTimestamp(),
          mergedFrom: selected.id,
        });
      }

      // Delete the source trip
      await deleteDoc(doc(db, "trips", selected.id));

      onMerged?.();
      onClose();
    } catch (e) {
      console.error("Merge failed:", e);
      setMerging(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Merge trips</div>
          <button className="modal-close" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>

        <div style={{ fontSize: 13, color: "var(--ink-light)", marginBottom: 16, lineHeight: 1.5 }}>
          All segments from the trip you choose below will be moved into <strong>{trip.name}</strong>, and the other trip will be deleted.
          {trip.startDate && (
            <span style={{ display: "block", marginTop: 4, color: "var(--ink-faint)" }}>
              Showing trips within 90 days of this trip's dates.
            </span>
          )}
          {isShared(trip) && (
            <span style={{ display: "block", marginTop: 4, color: "#C05621" }}>
              👥 This is a shared trip ({trip.memberIds.length} travelers). Anything moved into it will be visible to all of them.
            </span>
          )}
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: 24, color: "var(--ink-light)", fontSize: 13 }}>
            Loading trips...
          </div>
        )}

        {!loading && allTrips.length === 0 && (
          <div style={{ textAlign: "center", padding: 24, color: "var(--ink-light)", fontSize: 13 }}>
            No other trips found within 90 days of this trip.
          </div>
        )}

        {!loading && allTrips.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20, maxHeight: 320, overflowY: "auto" }}>
            {allTrips.map(t => (
              <div
                key={t.id}
                onClick={() => { if (isShared(t)) return; setSelected(t); setConfirm(false); }}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 14px",
                  background: selected?.id === t.id ? "var(--paper-warm)" : "var(--paper-card)",
                  border: `1.5px solid ${selected?.id === t.id ? "var(--accent)" : "var(--border)"}`,
                  borderRadius: "var(--radius-sm)",
                  cursor: isShared(t) ? "not-allowed" : "pointer",
                  opacity: isShared(t) ? 0.6 : 1,
                }}
              >
                <div style={{
                  width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                  background: t.color || "var(--ink-faint)",
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)" }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-light)" }}>
                    {t.startDate ? formatDate(t.startDate) : "No date"}
                    {t.endDate && t.endDate !== t.startDate ? ` — ${formatDate(t.endDate)}` : ""}
                  </div>
                  {isShared(t) && (
                    <div style={{ fontSize: 11, color: "#C05621", marginTop: 3, lineHeight: 1.4 }}>
                      👥 Shared — can't be merged away. Open that trip and merge into it instead.
                    </div>
                  )}
                </div>
                {selected?.id === t.id && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#C05621" }}>WILL BE DELETED</span>
                    <Icon name="check" size={14} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {selected && !confirm && (
          <button
            className="submit-btn"
            style={{ background: "#C05621" }}
            onClick={() => setConfirm(true)}
          >
            Merge "{selected.name}" into "{trip.name}"
          </button>
        )}

        {selected && confirm && (
          <>
            <div style={{
              background: "#FEF3EC", border: "1px solid #F6C9A8",
              borderRadius: "var(--radius-sm)", padding: "10px 14px",
              fontSize: 13, color: "#C05621", marginBottom: 12, lineHeight: 1.5,
            }}>
              ⚠️ This will permanently delete <strong>{selected.name}</strong> after moving its segments. This cannot be undone.
              {isShared(trip) && (
                <span style={{ display: "block", marginTop: 6 }}>
                  Its segments will become visible to all {trip.memberIds.length} travelers on <strong>{trip.name}</strong>.
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="seg-btn" onClick={() => setConfirm(false)} style={{ flex: 1 }}>
                Cancel
              </button>
              <button
                className="submit-btn"
                style={{ background: "#C05621", flex: 2 }}
                onClick={handleMerge}
                disabled={merging}
              >
                {merging ? "Merging..." : "Yes, merge and delete"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}